import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Wifi, Plus, Trash2, Copy, Check, ExternalLink, ToggleLeft, ToggleRight } from "lucide-react";

function generateTagId() {
  return "nfc_" + Math.random().toString(36).substring(2, 10) + Date.now().toString(36);
}

function getNFCUrl(venueId, tagId) {
  return `${window.location.origin}/NFCClock?venue=${venueId}&tag=${tagId}`;
}

export default function NFCSettings({ venueId }) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", location_hint: "" });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (!venueId) return;
    loadTags();
  }, [venueId]);

  const loadTags = async () => {
    setLoading(true);
    const all = await hosys.entities.NFCTag.filter({ venue_id: venueId });
    setTags(all.sort((a, b) => new Date(b.created_date) - new Date(a.created_date)));
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    await hosys.entities.NFCTag.create({
      venue_id: venueId,
      name: form.name.trim(),
      location_hint: form.location_hint.trim(),
      tag_id: generateTagId(),
      active: true,
    });
    setForm({ name: "", location_hint: "" });
    setAdding(false);
    await loadTags();
    setSaving(false);
  };

  const handleToggle = async (tag) => {
    await hosys.entities.NFCTag.update(tag.id, { active: !tag.active });
    await loadTags();
  };

  const handleDelete = async (tag) => {
    await hosys.entities.NFCTag.delete(tag.id);
    await loadTags();
  };

  const copyUrl = (url, tagId) => {
    navigator.clipboard.writeText(url);
    setCopied(tagId);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="space-y-5">
      {/* Explanation */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3">
        <Wifi className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-blue-800">Com funciona el fitxatge NFC</p>
          <p className="text-xs text-blue-700 mt-1">
            Cada etiqueta genera una URL única. Programa una etiqueta NFC (NTAG213/215) amb aquesta URL.
            Quan el treballador apropi el mòbil, s'obre la webapp i registra el fitxatge automàticament en menys de 2 segons.
          </p>
        </div>
      </div>

      {/* Add tag */}
      {!adding ? (
        <Button onClick={() => setAdding(true)} className="gap-2 bg-[#7B2D3F] hover:bg-[#5B1D2F]">
          <Plus className="w-4 h-4" /> Nova etiqueta NFC
        </Button>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3 shadow-sm">
          <p className="font-semibold text-gray-800 text-sm">Nova etiqueta NFC</p>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Nom *</label>
            <Input
              placeholder="Ex: Entrada principal"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Ubicació (opcional)</label>
            <Input
              placeholder="Ex: Porta d'entrada, Vestuaris..."
              value={form.location_hint}
              onChange={e => setForm(f => ({ ...f, location_hint: e.target.value }))}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCreate} disabled={saving || !form.name.trim()} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]" size="sm">
              {saving ? "Creant..." : "Crear etiqueta"}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setAdding(false); setForm({ name: "", location_hint: "" }); }}>
              Cancel·lar
            </Button>
          </div>
        </div>
      )}

      {/* Tags list */}
      {loading ? (
        <div className="text-center py-8 text-gray-400 text-sm">Carregant...</div>
      ) : tags.length === 0 ? (
        <div className="text-center py-10 text-gray-300">
          <Wifi className="w-10 h-10 mx-auto mb-2" />
          <p className="text-sm">Sense etiquetes NFC. Crea la primera!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tags.map(tag => {
            const url = getNFCUrl(venueId, tag.tag_id);
            const isCopied = copied === tag.tag_id;
            return (
              <div key={tag.id} className={`bg-white rounded-xl border shadow-sm p-4 ${!tag.active ? "opacity-60" : ""}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${tag.active ? "bg-[#7B2D3F]/10" : "bg-gray-100"}`}>
                      <Wifi className={`w-4 h-4 ${tag.active ? "text-[#7B2D3F]" : "text-gray-400"}`} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">{tag.name}</p>
                      {tag.location_hint && <p className="text-xs text-gray-400">{tag.location_hint}</p>}
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full mt-1 inline-block ${tag.active ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-400"}`}>
                        {tag.active ? "Activa" : "Inactiva"}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleToggle(tag)}
                      title={tag.active ? "Desactivar" : "Activar"}
                      className="p-1.5 text-gray-400 hover:text-[#7B2D3F] rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      {tag.active ? <ToggleRight className="w-5 h-5 text-[#7B2D3F]" /> : <ToggleLeft className="w-5 h-5" />}
                    </button>
                    <button
                      onClick={() => handleDelete(tag)}
                      className="p-1.5 text-gray-300 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* URL section */}
                <div className="mt-3 pt-3 border-t border-gray-50">
                  <p className="text-[10px] text-gray-400 font-medium mb-1.5 uppercase tracking-wide">URL per programar l'etiqueta</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-500 font-mono truncate border border-gray-100">
                      {url}
                    </div>
                    <button
                      onClick={() => copyUrl(url, tag.tag_id)}
                      className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-all flex-shrink-0 ${
                        isCopied ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {isCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      {isCopied ? "Copiat!" : "Copiar"}
                    </button>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium bg-[#7B2D3F]/10 text-[#7B2D3F] hover:bg-[#7B2D3F]/20 transition-all flex-shrink-0"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Provar
                    </a>
                  </div>
                  <p className="text-[10px] text-gray-300 mt-1.5">
                    ID: <span className="font-mono">{tag.tag_id}</span>
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Instructions */}
      {tags.length > 0 && (
        <div className="bg-gray-50 rounded-xl p-4 text-xs text-gray-500 space-y-1 border border-gray-100">
          <p className="font-semibold text-gray-700 mb-2">Com programar l'etiqueta física</p>
          <p>1. Descarrega l'app <strong>NFC Tools</strong> (iOS/Android) al teu mòbil.</p>
          <p>2. Selecciona "Escriure" → "Afegir registre" → "URL".</p>
          <p>3. Enganxa la URL de l'etiqueta i escriu-la a l'etiqueta NFC.</p>
          <p>4. Col·loca l'etiqueta a la ubicació del restaurant.</p>
          <p className="text-gray-400 pt-1">Compatible amb etiquetes NTAG213, NTAG215 i NTAG216.</p>
        </div>
      )}
    </div>
  );
}